/**
 * FleetOrderRow Component
 *
 * Displays a single order row in the FleetHub list.
 * Shows order number, number plate, status, inspection center, and action buttons.
 *
 * @module components/FleetOrderRow
 */

import { useState, useEffect } from "react"
import { View, ViewStyle, TextStyle, Pressable, useWindowDimensions } from "react-native"
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  withSequence,
  withDelay,
  withRepeat,
  interpolateColor,
} from "react-native-reanimated"
import Icon from "react-native-vector-icons/MaterialIcons"

import type { FleetOrder, PendingRowAction } from "@/stores/useFleetHubStore"
import { colors, spacing } from "@/theme"
import { coalesceProjectedTime, selectPickupLabel, selectReturnLabel } from "@/utils/time-display"

import {
  FleetOrderRowDefects,
  FleetOrderRowFin,
  FleetOrderRowNoPlate,
  type FleetOrderRowDefect,
} from "./FleetOrderRowExtras"
import { FleetOrderRowPendingOverlay } from "./FleetOrderRowPendingOverlay"
import { InspectionResultBadge } from "./InspectionResultBadge"
import { NumberPlateVisual } from "./NumberPlateVisual"
import { OrderProgressBar } from "./OrderProgressBar"
import { Text } from "./Text"

export interface FleetOrderRowProps {
  /**
   * Order data to display
   */
  order: FleetOrder

  /**
   * Callback for PDF download
   */
  onDownloadPdf?: (order: FleetOrder) => void

  /**
   * Callback for opening tracking
   */
  onOpenTracking?: (order: FleetOrder) => void

  /**
   * Callback for rescheduling
   */
  onReschedule?: (order: FleetOrder) => void

  /**
   * Callback for cancellation
   */
  onCancel?: (order: FleetOrder) => void

  /**
   * Callback for booking Nachkontrolle
   */
  onBookNachkontrolle?: (order: FleetOrder) => void

  /**
   * Callback for booking new inspection (for cancelled orders)
   */
  onBookNewInspection?: () => void

  /**
   * Optional style override
   */
  style?: ViewStyle

  /**
   * Whether this card should show a highlight glow animation
   */
  isHighlighted?: boolean

  /**
   * When set, a cancel/reschedule mutation is in flight for this row — renders
   * the pending overlay (dim + shimmer/caption while syncing, brand check pulse
   * once landed) and hides the action buttons so nothing is focusable under the
   * scrim.
   */
  pendingAction?: PendingRowAction

  /**
   * Opt-in (PSP view, PLA-861): replaces the time line under the date, e.g.
   * "09:40 Uhr · VW Golf". Absent → the regular time / ETA line.
   */
  metaLine?: string

  /** Opt-in (PSP view): FIN line under the order number. */
  fin?: string

  /** Opt-in (PSP view): search query to highlight inside the FIN. */
  finHighlight?: string

  /** Opt-in (PSP view): defect list under the result badge. */
  defects?: readonly FleetOrderRowDefect[]

  /** Opt-in (PSP view): chip shown instead of the plate when the car has none. */
  noPlateLabel?: string
}

/**
 * Get current phase number based on status and service type
 */
function getPhaseNumber(status: string, serviceType: string): number {
  // Cancelled orders show as phase 1
  if (status === "cancelled") return 1

  if (serviceType === "driveby") {
    // Driveby: 2 phases
    if (["completed", "ready_to_return", "returning"].includes(status)) {
      return 2
    }
    return 1
  }

  // Pickup/Delivery: 7 phases
  const phaseMap: Record<string, number> = {
    new: 1,
    confirmed: 2,
    picking_up: 3,
    at_hub: 4,
    ready_to_return: 5,
    returning: 6,
    completed: 7,
    backlog: 1,
    no_show: 1,
  }

  return phaseMap[status] || 1
}

/**
 * Get total phases based on service type
 */
function getTotalPhases(serviceType: string): number {
  return serviceType === "driveby" ? 2 : 7
}

/**
 * Get phase label in German
 */
function getPhaseLabel(status: string, serviceType: string): string {
  if (serviceType === "driveby") {
    const drivebyLabels: Record<string, string> = {
      new: "Termin gebucht",
      confirmed: "Termin gebucht",
      picking_up: "Termin gebucht",
      at_hub: "Termin gebucht",
      backlog: "Termin gebucht",
      pickup_due: "Termin gebucht",
      no_show: "Nicht angetroffen",
      cancelled: "Termin storniert",
      ready_to_return: "Prüfung durchgeführt",
      returning: "Prüfung durchgeführt",
      completed: "Prüfung durchgeführt",
    }
    return drivebyLabels[status] || status
  }

  const labels: Record<string, string> = {
    new: "Auftrag übermittelt",
    confirmed: "Auftrag bestätigt",
    picking_up: "Fahrer unterwegs",
    at_hub: "Fahrzeug in Prüfung",
    ready_to_return: "Prüfung abgeschlossen",
    returning: "Rücktransport läuft",
    completed: "Rücktransport abgeschlossen",
    no_show: "Nicht angetroffen",
    backlog: "Auftrag übermittelt",
    cancelled: "Storniert",
  }

  return labels[status] || status
}

/**
 * Get status accent color based on order status.
 * Uses theme tokens where possible. Dark green for completed is intentionally
 * not Signal Green (success): it marks a finished state, not a confirmation.
 */
function getStatusAccentColor(status: string): string {
  const colorMap: Record<string, string> = {
    completed: colors.statusCompleted, // Forest green — "finished" state, distinct from Signal Green confirmations
    picking_up: colors.primary, // Ignition Red — active live phase
    returning: colors.primary, // Ignition Red — active live phase
    at_hub: colors.text, // Dusknight
    ready_to_return: colors.text, // Dusknight
    no_show: colors.text, // Dusknight (never pure black per DESIGN.md)
    backlog: colors.textDim, // Graphite
    cancelled: colors.text, // Dusknight
  }

  return colorMap[status] || colors.textDim // Default Graphite (new, confirmed)
}

/**
 * Determine if inspection result should be shown
 * Only show when we have actual result information (passed/failed), not pending
 */
function shouldShowInspectionResult(inspectionPassed: boolean | null | undefined): boolean {
  // Only show if we have a definitive result (true or false), not null/undefined
  return inspectionPassed !== null && inspectionPassed !== undefined
}

/**
 * Check if number plate should be displayed
 * Returns false for empty, "ohne", or "ohne kennzeichen" values
 */
function shouldShowNumberPlate(numberPlate: string | undefined): boolean {
  if (!numberPlate) return false
  const normalized = numberPlate.toLowerCase().trim()
  return normalized !== "ohne" && normalized !== "ohne kennzeichen"
}

/**
 * Format ISO date string to DD.MM.YYYY
 */
function formatDate(dateString?: string): string {
  if (!dateString) return ""

  try {
    const date = new Date(dateString)
    const day = String(date.getDate()).padStart(2, "0")
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const year = date.getFullYear()
    return `${day}.${month}.${year}`
  } catch {
    return ""
  }
}

/**
 * FleetOrderRow Component
 */
export function FleetOrderRow({
  order,
  onDownloadPdf,
  onOpenTracking,
  onReschedule,
  onCancel,
  onBookNachkontrolle,
  onBookNewInspection,
  style: $styleOverride,
  isHighlighted = false,
  pendingAction,
  metaLine,
  fin,
  finHighlight,
  defects,
  noPlateLabel,
}: FleetOrderRowProps) {
  // Responsive detection
  const { width } = useWindowDimensions()
  const isMobile = width < 768

  // Highlight glow animation
  const highlightProgress = useSharedValue(0)

  useEffect(() => {
    if (isHighlighted) {
      highlightProgress.value = withSequence(
        withTiming(1, { duration: 200 }),
        withDelay(400, withTiming(0, { duration: 400 })),
      )
    }
  }, [isHighlighted])

  const animatedCardStyle = useAnimatedStyle(() => ({
    backgroundColor: interpolateColor(
      highlightProgress.value,
      [0, 1],
      [colors.surface, colors.highlightYellow],
    ),
  }))

  const currentPhase = getPhaseNumber(order.status, order.serviceType)
  const totalPhases = getTotalPhases(order.serviceType)
  const phaseLabel = getPhaseLabel(order.status, order.serviceType)

  const statusAccentColor = getStatusAccentColor(order.status)
  const isLive = ["confirmed", "picking_up", "at_hub", "ready_to_return", "returning"].includes(
    order.status,
  )

  // Live ETA cluster — shown only for pickup_delivery orders. The `now`
  // value used for the countdown is provided by the parent (FleetOrdersBottomSheet)
  // so a single timer ticks every row simultaneously.
  const showProjectedReturnCluster = order.serviceType === "pickup_delivery"

  // Phase-aware pickup / return selectors (ADR-0012 §6).
  const pickupResult = selectPickupLabel(order)
  const returnResult = selectReturnLabel(order)

  const projectedISO = coalesceProjectedTime(order)
  const projectedDate = projectedISO ? new Date(projectedISO) : null
  // PLA-307: when the order is completed and completedAt is present, the selector
  // returns kind "actual" — surface the exact time without "~" (it is recorded,
  // not estimated). For all other cases, prefix with "~" because the projected
  // return is a forecast, not a guarantee. Honest-time principle.
  const projectedTimeLabel = (() => {
    if (returnResult.kind === "actual" && returnResult.iso) {
      return new Date(returnResult.iso).toLocaleTimeString("de-DE", {
        timeZone: "Europe/Berlin",
        hour: "2-digit",
        minute: "2-digit",
      })
    }
    if (projectedDate) {
      return `~${projectedDate.toLocaleTimeString("de-DE", {
        timeZone: "Europe/Berlin",
        hour: "2-digit",
        minute: "2-digit",
      })}`
    }
    return order.spatesteRuckgabe ? `~${order.spatesteRuckgabe}` : "~--:--"
  })()

  // Return cluster label — "Rückgabe um" for recorded actual completion (PLA-307),
  // "Rückgabe gegen" when a live projection is present, else "Geplante Rückgabe".
  const returnClusterLabel =
    returnResult.kind === "actual"
      ? "Rückgabe um"
      : returnResult.kind === "projected"
        ? "Rückgabe gegen"
        : "Geplante Rückgabe"

  // Pickup meta line — phase-aware small text above the return cluster.
  // Drives off the selector's `kind` to surface the live projection during
  // picking_up (Codex P2 on PR #123). Falls back to the legacy
  // `spatesteAbholung` wall-clock string when the selector returns a null
  // ISO (older payloads carry only that field, not `scheduledPickupTime`) —
  // Codex P2 follow-up on PR #124.
  //   realised_pickup        → "HH:MM Uhr Abgeholt"     (post-pickup)
  //   projected_door_arrival → "~HH:MM Uhr Abholung"    (pre-pickup, live)
  //   scheduled_sla_ceiling  → "~HH:MM Uhr Abholung"    (pre-pickup, SLA ISO)
  //   any kind w/ null ISO   → "~{spatesteAbholung} Uhr Abholung" (legacy)
  //   none                   → null                       (driveby / no data)
  // Both phases use the "Uhr"-suffix grammar so the row stays scannable.
  // Driveby orders also short-circuit via showProjectedReturnCluster=false.
  const pickupMetaLine: string | null = (() => {
    if (!showProjectedReturnCluster) return null
    if (pickupResult.kind === "none") return null
    const formatHHMM = (iso: string) =>
      new Date(iso).toLocaleTimeString("de-DE", {
        timeZone: "Europe/Berlin",
        hour: "2-digit",
        minute: "2-digit",
      })
    if (pickupResult.kind === "realised_pickup" && pickupResult.iso) {
      return `${formatHHMM(pickupResult.iso)} Uhr Abgeholt`
    }
    if (pickupResult.iso) {
      // projected_door_arrival or scheduled_sla_ceiling with a real ISO.
      return `~${formatHHMM(pickupResult.iso)} Uhr Abholung`
    }
    // Legacy fallback: selector handed back a null ISO (no scheduledPickupTime
    // on the payload). Render the wall-clock `spatesteAbholung` value the
    // backend still ships.
    return order.spatesteAbholung ? `~${order.spatesteAbholung} Uhr Abholung` : null
  })()

  // LiveTrackingBadge-style ripple effect for live status dot
  const dotOpacity = useSharedValue(1)
  const rippleScale = useSharedValue(1)
  const rippleOpacity = useSharedValue(0)

  useEffect(() => {
    if (isLive) {
      // Inner dot: opacity pulse 1→0.6→1 over 2s
      dotOpacity.value = withRepeat(
        withSequence(withTiming(0.6, { duration: 1000 }), withTiming(1, { duration: 1000 })),
        -1,
      )
      // Ripple ring: scale 1→3, opacity 0.8→0 over 2s, then reset
      rippleScale.value = withRepeat(
        withSequence(withTiming(1, { duration: 0 }), withTiming(3, { duration: 2000 })),
        -1,
      )
      rippleOpacity.value = withRepeat(
        withSequence(withTiming(0.8, { duration: 0 }), withTiming(0, { duration: 2000 })),
        -1,
      )
    } else {
      dotOpacity.value = 1
      rippleScale.value = 1
      rippleOpacity.value = 0
    }
  }, [isLive])

  const animatedDotStyle = useAnimatedStyle(() => ({
    opacity: dotOpacity.value,
  }))

  const animatedRippleStyle = useAnimatedStyle(() => ({
    transform: [{ scale: rippleScale.value }],
    opacity: rippleOpacity.value,
  }))

  // Check if order is in final phase - show Nachkontrolle button regardless of inspection result
  const canBookNachkontrolle = currentPhase === totalPhases

  // Check if order can be rescheduled - early phases only, NOT cancelled
  const canReschedule =
    order.status !== "cancelled" &&
    ((order.serviceType === "pickup_delivery" && [1, 2].includes(currentPhase)) ||
      (order.serviceType === "driveby" && currentPhase === 1))

  // Check if order can be cancelled - same logic as reschedule, but NOT if already cancelled
  const canCancel =
    order.status !== "cancelled" &&
    ((order.serviceType === "pickup_delivery" && [1, 2].includes(currentPhase)) ||
      (order.serviceType === "driveby" && currentPhase === 1))

  // Check if order is cancelled - show "Neu buchen" button
  const isCancelled = order.status === "cancelled"

  return (
    <Animated.View style={[$cardOuter, $styleOverride, animatedCardStyle]}>
      {/* Card content */}
      <View style={$cardInner}>
        <View style={isMobile ? $contentContainerMobile : $contentContainer}>
          {/* Left Section: Order Number & Number Plate */}
          <View style={isMobile ? $leftSectionMobile : $leftSection}>
            {isMobile ? (
              <>
                {/* Mobile: Horizontal layout with plate on right */}
                <View style={$mobileLeftHeader}>
                  <View style={[$mobileOrderInfo, { gap: 2 }]}>
                    {/* Date */}
                    {order.scheduledPickupTime && (
                      <Text style={$orderNumber}>{formatDate(order.scheduledPickupTime)}</Text>
                    )}
                    {/* Pickup time + projected return cluster (pickup_delivery only) */}
                    {metaLine ? (
                      <Text style={$detailText} numberOfLines={1}>
                        {metaLine}
                      </Text>
                    ) : showProjectedReturnCluster ? (
                      <View style={{ gap: 2 }}>
                        {pickupMetaLine && (
                          <Text style={$detailText} numberOfLines={1}>
                            {pickupMetaLine}
                          </Text>
                        )}
                        <View style={$etaClusterRow}>
                          <View style={{ flex: 1 }}>
                            <Text style={$etaClusterLabel} numberOfLines={1}>
                              {returnClusterLabel}
                            </Text>
                            <Text style={$etaClusterTime} numberOfLines={1}>
                              {projectedTimeLabel}
                            </Text>
                          </View>
                        </View>
                      </View>
                    ) : (
                      (order.spatesteAbholung || order.spatesteRuckgabe) && (
                        <Text style={$detailText} numberOfLines={1}>
                          ~{order.spatesteAbholung || "??:??"} - {order.spatesteRuckgabe || "??:??"}{" "}
                          Uhr
                        </Text>
                      )
                    )}
                  </View>
                  {!shouldShowNumberPlate(order.numberPlate) && noPlateLabel && (
                    <FleetOrderRowNoPlate label={noPlateLabel} />
                  )}
                  {shouldShowNumberPlate(order.numberPlate) && (
                    <View style={$mobilePlateContainer}>
                      <NumberPlateVisual
                        numberPlate={order.numberPlate}
                        sizeMode="tiny"
                        showRedBand={true}
                        plateBackgroundColor={colors.surface}
                        inputBackgroundColor={colors.surface}
                        textColor={colors.palette.neutral900}
                        fontSize={16}
                      />
                    </View>
                  )}
                </View>
              </>
            ) : (
              <>
                {/* Desktop: Vertical layout with plate at bottom */}
                <View style={{ gap: 4 }}>
                  {/* Date */}
                  {order.scheduledPickupTime && (
                    <Text style={$orderNumber}>{formatDate(order.scheduledPickupTime)}</Text>
                  )}
                  {/* Pickup time + projected return cluster (pickup_delivery only) */}
                  {metaLine ? (
                    <Text style={$detailText}>{metaLine}</Text>
                  ) : showProjectedReturnCluster ? (
                    <View style={{ gap: 4 }}>
                      {pickupMetaLine && <Text style={$detailText}>{pickupMetaLine}</Text>}
                      <View style={$etaClusterRow}>
                        <View style={{ flex: 1 }}>
                          <Text style={$etaClusterLabel}>{returnClusterLabel}</Text>
                          <Text style={$etaClusterTime}>{projectedTimeLabel}</Text>
                        </View>
                      </View>
                    </View>
                  ) : (
                    (order.spatesteAbholung || order.spatesteRuckgabe) && (
                      <Text style={$detailText}>
                        ~{order.spatesteAbholung || "??:??"} - {order.spatesteRuckgabe || "??:??"}{" "}
                        Uhr
                      </Text>
                    )
                  )}
                </View>
                {/* Spacer to push plate to bottom */}
                <View style={{ flex: 1 }} />
                {!shouldShowNumberPlate(order.numberPlate) && noPlateLabel && (
                  <FleetOrderRowNoPlate label={noPlateLabel} />
                )}
                {shouldShowNumberPlate(order.numberPlate) && (
                  <View style={$desktopPlateContainer}>
                    <NumberPlateVisual
                      numberPlate={order.numberPlate}
                      sizeMode="compact"
                      showRedBand={true}
                      plateBackgroundColor={colors.surface}
                      inputBackgroundColor={colors.surface}
                      textColor={colors.palette.neutral900}
                      fontSize={14}
                    />
                  </View>
                )}
              </>
            )}
          </View>

          {/* Center Section: Status & Details */}
          <View style={$centerSection}>
            {/* Progress Bar — hidden for cancelled orders */}
            {order.status !== "cancelled" && (
              <OrderProgressBar
                currentPhase={currentPhase}
                isDriveby={order.serviceType === "driveby"}
                style={$progressBar}
              />
            )}

            {/* Phase Label */}
            {order.status === "cancelled" ? (
              <View style={$cancelledChip}>
                <Text style={$cancelledChipText}>Storniert</Text>
              </View>
            ) : (
              <View style={$phaseRow}>
                <View style={$statusDotContainer}>
                  {isLive && (
                    <Animated.View
                      style={[
                        $statusRipple,
                        { backgroundColor: statusAccentColor },
                        animatedRippleStyle,
                      ]}
                    />
                  )}
                  {isLive ? (
                    <Animated.View
                      style={[$statusDot, { backgroundColor: statusAccentColor }, animatedDotStyle]}
                    />
                  ) : (
                    <View style={[$statusDot, { backgroundColor: statusAccentColor }]} />
                  )}
                </View>
                <Text style={$phaseText}>
                  {phaseLabel} - Phase {currentPhase}/{totalPhases}
                </Text>
              </View>
            )}

            {/* Inspection Center */}
            {order.zustandigePrufstelle && (
              <Text style={$detailText}>Prüfstelle: {order.zustandigePrufstelle}</Text>
            )}

            {/* Order Number - below Prüfstelle */}
            <Text style={$detailText}>Auftragsnummer: #{order.id}</Text>

            {/* Opt-in FIN line (PSP view) */}
            {fin && <FleetOrderRowFin fin={fin} highlight={finHighlight} />}

            {/* Child Company Location (if applicable) */}
            {order.childCompanyName && (
              <View style={$locationBadgeContainer}>
                <Icon name="business" size={14} color={colors.primary} />
                <Text style={$locationBadge}>{order.childCompanyName}</Text>
              </View>
            )}

            {/* Inspection Result - only show when we have actual result (not pending) */}
            {shouldShowInspectionResult(order.inspectionPassed) && (
              <InspectionResultBadge
                passed={order.inspectionPassed}
                result={order.inspectionResult}
                style={$badge}
              />
            )}

            {/* Opt-in defect list (PSP view) */}
            {defects && defects.length > 0 && <FleetOrderRowDefects defects={defects} />}
          </View>

          {/* Divider above actions on mobile */}
          {isMobile && <View style={$mobileActionsDivider} />}

          {/* Right Section: Action Buttons — hidden while a row mutation is pending
              (the overlay scrim covers the row; nothing focusable underneath) */}
          <View style={isMobile ? $rightSectionMobile : $rightSection}>
            {!pendingAction && (
              <>
                {/* PDF Download */}
                {order.berichtPdfStatus === "available" && onDownloadPdf && (
                  <ActionButton
                    iconName="description"
                    label="Bericht"
                    onPress={() => onDownloadPdf(order)}
                    tooltip="Bericht herunterladen"
                    isMobile={isMobile}
                  />
                )}

                {/* Tracking Link */}
                {order.trackingToken && onOpenTracking && (
                  <ActionButton
                    iconName="open-in-new"
                    label="Tracking"
                    onPress={() => onOpenTracking(order)}
                    tooltip="Tracking öffnen"
                    isMobile={isMobile}
                  />
                )}

                {/* Nachkontrolle buchen - for orders in final phase (7/7 or 2/2) */}
                {canBookNachkontrolle && onBookNachkontrolle && (
                  <ActionButton
                    iconName="add-circle-outline"
                    label="Nachkontrolle buchen"
                    onPress={() => onBookNachkontrolle(order)}
                    tooltip="Nachkontrolle buchen"
                    isMobile={isMobile}
                  />
                )}

                {/* Reschedule - for orders in early phases (1-2 for pickup/delivery, 1 for driveby) */}
                {canReschedule && onReschedule && (
                  <ActionButton
                    iconName="event"
                    label="Verschieben"
                    onPress={() => onReschedule(order)}
                    tooltip="Termin verschieben"
                    isMobile={isMobile}
                  />
                )}

                {/* Cancel - for orders in early phases (1-2 for pickup/delivery, 1 for driveby) */}
                {canCancel && onCancel && (
                  <ActionButton
                    iconName="cancel"
                    label="Stornieren"
                    onPress={() => onCancel(order)}
                    tooltip="Auftrag stornieren"
                    isMobile={isMobile}
                  />
                )}

                {/* Book New Inspection - for cancelled orders */}
                {isCancelled && onBookNewInspection && (
                  <ActionButton
                    iconName="add-circle-outline"
                    label="Neu buchen"
                    onPress={onBookNewInspection}
                    tooltip="Neue Inspektion buchen"
                    isMobile={isMobile}
                  />
                )}
              </>
            )}
          </View>
        </View>
      </View>

      {/* Pending overlay — cancel/reschedule sync (last child, layers on top of
          the card via $cardOuter's overflow:hidden + position:relative) */}
      {pendingAction && (
        <FleetOrderRowPendingOverlay type={pendingAction.type} phase={pendingAction.phase} />
      )}
    </Animated.View>
  )
}

/**
 * Action Button Sub-component
 */
interface ActionButtonProps {
  iconName: string // MaterialIcons icon name
  label: string
  onPress: () => void
  tooltip?: string
  isMobile?: boolean
}

function ActionButton({ iconName, label, onPress, tooltip, isMobile }: ActionButtonProps) {
  const [isHovered, setIsHovered] = useState(false)

  return (
    <Pressable
      onPress={onPress}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      style={({ pressed }) => [
        isMobile ? $actionButtonMobile : $actionButton,
        isHovered && $actionButtonHovered,
        pressed && $actionButtonPressed,
      ]}
      accessibilityLabel={tooltip || label}
      accessibilityRole="button"
    >
      <Icon name={iconName} size={20} color={colors.textDim} style={$actionIcon} />
      <Text style={$actionLabel} numberOfLines={1} ellipsizeMode="tail">
        {label}
      </Text>
    </Pressable>
  )
}

// Styles
// Card outer container — Dusknight-tinted layered shadow, no pure black
const $cardOuter: ViewStyle = {
  marginBottom: spacing.sm, // 12 — matches spacing scale (was 10, off-scale)
  borderRadius: 14,
  backgroundColor: colors.surface,
  // Layered shadow — Dusknight tint at low opacity, never pure black (DESIGN.md Brand-Tinted Shadow Rule)
  shadowColor: "rgba(17, 30, 44, 0.06)",
  shadowOffset: { width: 0, height: 2 },
  shadowOpacity: 1,
  shadowRadius: 12,
  elevation: 2,
  borderWidth: 1,
  borderColor: "rgba(17, 30, 44, 0.04)",
  overflow: "hidden",
  position: "relative",
  // @ts-ignore - Web layered shadow (RN style cannot express two shadows; this is the web override)
  boxShadow: "0 1px 2px rgba(17, 30, 44, 0.04), 0 4px 12px rgba(17, 30, 44, 0.06)",
  // @ts-ignore
  transition: "border-color 150ms ease, box-shadow 150ms ease",
}

// Card inner container
const $cardInner: ViewStyle = {
  backgroundColor: colors.surface, // Surface White token
  paddingHorizontal: spacing.md, // 16 — was 14, off-scale
  paddingVertical: spacing.sm, // 12
}

const $contentContainer: ViewStyle = {
  flexDirection: "row",
  gap: spacing.sm, // 12 — was 10, off-scale
}

// Mobile-specific content container
const $contentContainerMobile: ViewStyle = {
  flexDirection: "column",
  gap: spacing.sm,
}

// Left section — fixed-width time anchor. No hairline divider on the right;
// spacing carries the section separation (Uber/Bolt row convention).
const $leftSection: ViewStyle = {
  width: 150,
  flexShrink: 0,
  flexDirection: "column",
  justifyContent: "flex-start",
  alignItems: "flex-start",
  paddingRight: spacing.sm, // 12
  paddingVertical: spacing.xxs, // 4 — was 6, off-scale
}

// Mobile left section - full width
const $leftSectionMobile: ViewStyle = {
  width: "100%", // Full width on mobile
  flexShrink: 0,
  justifyContent: "center",
  paddingRight: 10,
  paddingVertical: 6,
}

// Status dot container for ripple effect
const $statusDotContainer: ViewStyle = {
  width: 16,
  height: 16,
  alignItems: "center",
  justifyContent: "center",
}

// Ripple ring — expands behind the dot
const $statusRipple: ViewStyle = {
  position: "absolute",
  width: 8,
  height: 8,
  borderRadius: 4,
}

// Status dot — replaces the left accent bar
const $statusDot: ViewStyle = {
  width: 8,
  height: 8,
  borderRadius: 4,
}

const $phaseRow: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: 8,
}

// Order number / date — Medium weight so status (600) can carry hierarchy
const $orderNumber: TextStyle = {
  fontSize: 13,
  fontWeight: "500",
  color: colors.text,
  marginBottom: 2,
  letterSpacing: 0, // Workaround for React Native Web text node error
}

// Mobile layout: horizontal header with plate on right
const $mobileLeftHeader: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
}

const $mobileOrderInfo: ViewStyle = {
  flexDirection: "column",
  flex: 1,
}

const $mobilePlateContainer: ViewStyle = {
  width: 160, // Double the original size for better mobile visibility
  height: 36,
  marginLeft: 8,
}

// Desktop layout: plate at bottom, aligned left
const $desktopPlateContainer: ViewStyle = {
  width: 135, // Larger size for better readability
  height: 32, // Increased to accommodate 14px font
  marginTop: 6,
}

// Center section — flexible, holds progress + status + meta. No vertical
// dividers; rhythm via gap.
const $centerSection: ViewStyle = {
  flex: 1,
  gap: spacing.xs - 2, // 6 — tight gap inside section
  paddingHorizontal: spacing.sm, // 12 — was 10, off-scale
  paddingVertical: spacing.xxs, // 4
}

const $progressBar: ViewStyle = {
  marginVertical: spacing.xs - 2, // 6
}

// Phase status — the visual anchor of the row. SemiBold so the eye lands here
// first (Uber/Bolt status-first convention; DESIGN.md hierarchy).
const $phaseText: TextStyle = {
  fontSize: 14,
  fontWeight: "600",
  color: colors.text,
  letterSpacing: 0, // Workaround for React Native Web text node error
}

const $cancelledChip: ViewStyle = {
  backgroundColor: "rgba(255, 56, 47, 0.08)",
  paddingHorizontal: 10,
  paddingVertical: 3,
  borderRadius: 12,
  alignSelf: "flex-start",
}

const $cancelledChipText: TextStyle = {
  fontSize: 12,
  fontWeight: "600",
  color: colors.primary,
  letterSpacing: 0,
}

const $detailText: TextStyle = {
  fontSize: 12,
  color: colors.textDim,
  lineHeight: 17,
  letterSpacing: 0,
}

// ETA cluster — pickup line above, prominent projected-return time below. The
// thin top divider is intentional inside the left section; it groups the date
// + pickup line as meta, and the ETA below as the anchor.
const $etaClusterRow: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: spacing.xs, // 8
  marginTop: 2,
  paddingTop: spacing.xxs, // 4
  borderTopWidth: 1,
  borderTopColor: "rgba(17, 30, 44, 0.06)", // softened from 0.08
}

// Uppercase mini-label above the ETA, "Voraussichtlich zurück" / "Geplante Rückgabe".
// Bumped from 9 → 10 for legibility; the Uber pattern uses ~10px uppercase.
const $etaClusterLabel: TextStyle = {
  fontSize: 10,
  color: colors.textDim,
  textTransform: "uppercase",
  letterSpacing: 0.6,
  fontWeight: "600",
}

// ETA — prominent but not louder than allowed. SemiBold (600) is the ceiling
// per DESIGN.md Bold-Means-Action Rule; the 800 fallback was rendering as
// whichever weight Plus Jakarta Sans happened to load nearest. Bumping size to
// 20 to retain visual presence after the weight drop.
const $etaClusterTime: TextStyle = {
  fontSize: 20,
  fontWeight: "600",
  color: colors.primary,
  letterSpacing: -0.2,
  lineHeight: 24,
  marginTop: 1,
}

const $badge: ViewStyle = {
  marginTop: 6, // Reduced from 10
}

const $locationBadgeContainer: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: 4,
  marginTop: 4,
}

const $locationBadge: TextStyle = {
  fontSize: 11,
  fontWeight: "600",
  color: colors.primary,
  letterSpacing: 0,
}

// Right section — actions cluster. No left hairline; spacing carries the
// separation (Uber/Bolt row convention).
const $rightSection: ViewStyle = {
  width: 165,
  flexShrink: 0,
  flexDirection: "column",
  gap: spacing.xxs, // 4
  alignItems: "stretch",
  paddingLeft: spacing.sm, // 12
  paddingVertical: spacing.xxs, // 4 — was 6, off-scale
}

// Hairline divider above mobile actions
const $mobileActionsDivider: ViewStyle = {
  height: 1,
  backgroundColor: "rgba(17, 30, 44, 0.06)",
  width: "100%",
  marginTop: 4,
  marginBottom: 2,
}

// Mobile right section - 2-column wrap grid
const $rightSectionMobile: ViewStyle = {
  width: "100%",
  flexShrink: 0,
  flexDirection: "row",
  flexWrap: "wrap",
  gap: 6,
  alignItems: "stretch",
  paddingHorizontal: 4,
  paddingTop: 2,
  paddingBottom: 4,
}

const $actionButton: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "flex-start",
  paddingVertical: 6,
  paddingHorizontal: 8,
  borderRadius: 8,
  backgroundColor: "transparent",
  flexShrink: 0,
  overflow: "hidden",
  cursor: "pointer" as any,
  // @ts-ignore
  transition: "all 150ms ease",
}

// Mobile action button - half-width in 2-column grid
const $actionButtonMobile: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  paddingVertical: spacing.xs, // 8
  paddingHorizontal: spacing.sm, // 12 — was 10
  borderRadius: 8,
  backgroundColor: colors.surfaceSecondary, // Mist (was hardcoded #F8F8F8)
  // @ts-ignore - web percentage
  flexBasis: "47%",
  flexGrow: 1,
  minHeight: 40,
  overflow: "hidden",
  cursor: "pointer" as any,
  // @ts-ignore
  transition: "all 150ms ease",
}

const $actionButtonHovered: ViewStyle = {
  backgroundColor: colors.surfaceSecondary, // Mist
}

const $actionButtonPressed: ViewStyle = {
  backgroundColor: colors.palette.neutral300, // Soft Edge — pressed darker than hover
}

const $actionIcon: ViewStyle = {
  marginRight: spacing.xs - 2, // 6 — keep optical alignment between icon and label
}

const $actionLabel: TextStyle = {
  fontSize: 12, // Bumped from 11 to match $detailText scale
  fontWeight: "500",
  color: colors.textDim,
  letterSpacing: 0, // Workaround for React Native Web text node error
}
